import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { PaymentStepConfig } from '@/api';
import Button from '@/design_system/Button';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import { RequestWithRelations, useStartPayment } from '@/models/request';
import { hasPermission } from '@/models/user';
import { useCurrentSession } from '@/services/auth';

import { ExternalPaymentOrRefundModal } from './ExternalPaymentOrRefundModal/ExternalPaymentOrRefundModal';
import { PointOfSalesPaymentModal } from './PointOfSalesPaymentModal';

export const ValidatePaymentAction = ({
  request,
  onActionDone,
}: {
  request: RequestWithRelations;
  onActionDone: () => void;
}) => {
  const { currentSession } = useCurrentSession();

  const stepConfig = request.articles.find((article) => article.step?.step === 'payment')?.step
    ?.config as PaymentStepConfig['config'];

  if (!stepConfig.method || !['external', 'point-of-sales'].includes(stepConfig.method)) {
    return null;
  }

  if (
    !hasPermission(currentSession, 'validate_payment', {
      organizationId: request.organizationId,
      entityId: request.storeId,
    })
  ) {
    return null;
  }

  if (stepConfig.method === 'point-of-sales') {
    return <PointOfSalesActionBar request={request} onActionDone={onActionDone} />;
  }

  return <ExternalActionBar onActionDone={onActionDone} />;
};

const ExternalActionBar = ({ onActionDone }: { onActionDone: () => void }) => {
  const [isConfirmClientPaymentModalOpen, setIsConfirmClientPaymentModalOpen] = useState(false);

  return (
    <>
      <Footer>
        <FooterButtons>
          <Button onPress={() => setIsConfirmClientPaymentModalOpen(true)}>
            <Trans id="validate-payment-action.confirm-payment.button">
              Confirm client payment
            </Trans>
          </Button>
        </FooterButtons>
      </Footer>

      {isConfirmClientPaymentModalOpen && (
        <ExternalPaymentOrRefundModal
          onClose={() => setIsConfirmClientPaymentModalOpen(false)}
          onActionDone={onActionDone}
          step="payment"
        />
      )}
    </>
  );
};

const PointOfSalesActionBar = ({
  request,
  onActionDone,
}: {
  request: RequestWithRelations;
  onActionDone: () => void;
}) => {
  const { t } = useLingui();
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: startPayment, isPending: isStartPaymentPending } = useStartPayment();

  const handleSendPaymentOrder = async () => {
    try {
      setError(null);
      const { transactionId: newTransactionId } = await startPayment({ requestId: request.id });

      setTransactionId(newTransactionId);
    } catch (err: any) {
      setError(
        (err.message as string) ??
          t({ id: 'request.pos-payment.error-send-order', message: 'Failed to send payment order' })
      );
    }
  };

  return (
    <>
      <Footer>
        {error && <FooterError>{error}</FooterError>}
        <FooterButtons>
          <Button
            onPress={handleSendPaymentOrder}
            isLoading={isStartPaymentPending || !!transactionId}
          >
            <Trans id="request.pos-payment.send-order">Send payment order</Trans>
          </Button>
        </FooterButtons>
      </Footer>
      <PointOfSalesPaymentModal
        transactionId={transactionId}
        onClose={() => {
          setTransactionId(null);
          onActionDone();
        }}
      />
    </>
  );
};
